"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheck, FaBell } from "react-icons/fa6";
import { BsExclamationCircle } from "react-icons/bs";
import { FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { hideToast } from "../redux/slices/toastSlice";



export default function Toast({
  duration = 2500,
  maxWidth = "max-w-md",
}) {
  // portal root
  useEffect(() => {
    let el = document.getElementById("toast-root");
    if (!el) {
      el = document.createElement("div");
      el.id = "toast-root";
      document.body.appendChild(el);
    }
  }, []);

  const dispatch = useDispatch();
  const isOpen = useSelector((s) => s.toast?.show);
  const message = useSelector((s) => s.toast?.message);
  const type = useSelector((s) => s.toast?.type);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // timer management
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const elapsedRef = useRef(0);
  const remainingRef = useRef(duration);
  const pausedRef = useRef(false);
  const [progress, setProgress] = useState(0); // 0..1

  useEffect(() => {
    remainingRef.current = duration;
    elapsedRef.current = 0;
    setProgress(0);
  }, [duration]);

  // keyboard escape to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && isOpen) handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // start / stop timer when open changes
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(timerRef.current);

    if (!isOpen) {
      remainingRef.current = duration;
      elapsedRef.current = 0;
      pausedRef.current = false;
      setProgress(0);
      return;
    }

    // start
    startRef.current = performance.now();
    pausedRef.current = false;

    const tick = (now) => {
      if (pausedRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const delta = now - startRef.current + elapsedRef.current; // ms
      const capped = Math.min(delta, duration);
      setProgress(Math.min(1, capped / duration));
      if (capped >= duration) {
        handleClose();
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    timerRef.current = setTimeout(() => handleClose(), remainingRef.current);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(timerRef.current);
    };
  }, [isOpen, duration]);

  const handleClose = () => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(timerRef.current);
    remainingRef.current = duration;
    elapsedRef.current = 0;
    pausedRef.current = false;
    setProgress(0);
    dispatch(hideToast());
  };

  const pause = () => {
    if (!isOpen || pausedRef.current) return;
    pausedRef.current = true;
    // accumulate elapsed so far
    elapsedRef.current = Math.min(duration, (performance.now() - startRef.current) + elapsedRef.current);
    remainingRef.current = Math.max(0, duration - elapsedRef.current);
    clearTimeout(timerRef.current);
  };

  const resume = () => {
    if (!isOpen || !pausedRef.current) return;
    pausedRef.current = false;
    startRef.current = performance.now();
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => handleClose(), remainingRef.current);
  };

  if (typeof document === "undefined") return null;

  const tokens = {
    success: {
      ring: "from-[#DFF6E5] to-[#E6F9ED]",
      accent: "text-emerald-600",
      bg: "bg-white",
      gradient: "bg-gradient-to-br from-emerald-50 to-white",
      iconBg: "bg-emerald-100/60",
    },
    warning: {
      ring: "from-[#FFF5DB] to-[#FFF1D6]",
      accent: "text-yellow-600",
      bg: "bg-white",
      gradient: "bg-gradient-to-br from-yellow-50 to-white",
      iconBg: "bg-yellow-100/60",
    },
    error: {
      ring: "from-[#FFEDED] to-[#FFE9E9]",
      accent: "text-rose-600",
      bg: "bg-white",
      gradient: "bg-gradient-to-br from-rose-50 to-white",
      iconBg: "bg-rose-100/60",
    },
    default: {
      ring: "from-[#EBF1FF] to-[#F3F7FF]",
      accent: "text-sky-600",
      bg: "bg-white",
      gradient: "bg-gradient-to-br from-sky-50 to-white",
      iconBg: "bg-sky-100/60",
    },
  };

  const t = tokens[type] || tokens.default;

  // Framer motion variants
  const containerVariants = {
    hidden: { opacity: 0, y: 12, scale: 0.985 },
    visible: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 12, scale: 0.985, transition: { duration: 0.18 } },
  };

  const DesktopToast = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="centered-toast"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={{
            ...containerVariants,
            hidden: { opacity: 0 },
            visible: { opacity: 1 },
            exit: { opacity: 0 },
          }}
          transition={{ duration: 0.16 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          aria-live="polite"
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            role="button"
            aria-label="بستن توست"
          />

          <motion.div
            onMouseEnter={pause}
            onMouseLeave={resume}
            onClick={(e) => e.stopPropagation()}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={containerVariants}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className={`${maxWidth} w-full ${t.bg} ${t.gradient} rounded-2xl shadow-2xl p-5 md:p-6 relative flex flex-col gap-3`}
            style={{
              boxShadow: "0 20px 50px rgba(2,6,23,0.18), inset 0 1px 0 rgba(255,255,255,0.5)",
            }}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <div className="flex items-start gap-4">
              <div className={`relative flex items-center justify-center w-12 h-12 rounded-xl ${t.iconBg} ring-1 ring-white/30 shadow-sm shrink-0`}>
                {type === "success" ? (
                  <FaCheck className="text-emerald-600" size={18} />
                ) : type === "error" ? (
                  <BsExclamationCircle className="text-rose-600" size={18} />
                ) : type === "warning" ? (
                  <BsExclamationCircle className="text-yellow-600" size={18} />
                ) : (
                  <FaBell className="text-sky-600" size={18} />
                )}
                {/* decorative glow */}
                <span className={`absolute -inset-1 rounded-xl opacity-30 blur-sm ${type === "success" ? "bg-emerald-200" : type === "error" ? "bg-rose-200" : type === "warning" ? "bg-yellow-200" : "bg-sky-200"}`} />
              </div>

              <div className="flex-1 items-center">
                <div className="flex items-start justify-between gap-3">
                    <span className={`text-sm font-semibold text-center ${t.accent} text-right`}>{message || (type === "success" ? "عملیات با موفقیت انجام شد" : "آگاه سازی")}</span>

                  <button
                    onClick={handleClose}
                    className="ml-2 rounded-full p-1.5 hover:bg-black/5 transition"
                    aria-label="بستن"
                    title="بستن"
                  >
                    <FiX size={16} className="text-gray-500" />
                  </button>
                </div>
              </div>
            </div>

            {/* progress bar */}
            <div className="h-1 w-full rounded-full bg-black/5 overflow-hidden mt-1">
              <div
                className="h-full rounded-full transform origin-left transition-all ease-linear"
                style={{
                  width: `${Math.min(100, Math.round(progress * 100))}%`,
                  background:
                    type === "success"
                      ? "linear-gradient(90deg,#10b981,#34d399)"
                      : type === "error"
                        ? "linear-gradient(90deg,#fb7185,#f97316)"
                        : type === "warning"
                          ? "linear-gradient(90deg,#f59e0b,#fbbf24)"
                          : "linear-gradient(90deg,#0ea5e9,#60a5fa)",
                  transition: "width 100ms linear",
                }}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const MobileToast = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="mobile-toast"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.12 }}
          className="fixed inset-0 pointer-events-none z-50"
          aria-live="polite"
        >
          <div className="w-full h-full flex pointer-events-none items-end justify-center pb-6 px-3">
            <motion.div
              onMouseEnter={pause}
              onMouseLeave={resume}
              onTouchStart={pause}
              onTouchEnd={resume}
              onClick={(e) => {
                e.stopPropagation();
                // tap closes
                handleClose();
              }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 200 }}
              dragElastic={0.2}
              onDragEnd={(e, info) => {
                // if pulled down fast enough or far enough => close
                if (info.offset.y > 80 || info.velocity.y > 800) {
                  handleClose();
                }
              }}
              initial={{ y: 50, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className={`pointer-events-auto ${maxWidth} w-full ${t.bg} ${t.gradient} rounded-2xl shadow-lg border border-gray-100 flex gap-3 items-center p-3 md:p-4 mx-2`}
              style={{
                boxShadow: "0 10px 30px rgba(2,6,23,0.14)",
              }}
            >
              <div className={`flex items-center justify-center w-11 h-11 rounded-lg ${t.iconBg} shrink-0`}>
                {type === "success" ? (
                  <FaCheck className="text-emerald-600" size={18} />
                ) : type === "error" ? (
                  <BsExclamationCircle className="text-rose-600" size={18} />
                ) : type === "warning" ? (
                  <BsExclamationCircle className="text-yellow-600" size={18} />
                ) : (
                  <FaBell className="text-sky-600" size={18} />
                )}
              </div>

              <div className="flex-1 text-right">
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`text-sm font-medium ${t.accent}`}>{message || (type === "success" ? "عملیات موفق" : "اطلاع")}</div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClose();
                    }}
                    aria-label="بستن"
                    className="p-1 rounded-md hover:bg-black/5"
                  >
                    <FiX size={16} className="text-gray-500" />
                  </button>
                </div>

                {/* mobile progress */}
                <div className="mt-2 h-1 w-full rounded-full bg-black/5 overflow-hidden">
                  <div
                    style={{
                      width: `${Math.min(100, Math.round(progress * 100))}%`,
                      height: "100%",
                      borderRadius: 9999,
                      transition: "width 100ms linear",
                      background:
                        type === "success"
                          ? "linear-gradient(90deg,#10b981,#34d399)"
                          : type === "error"
                            ? "linear-gradient(90deg,#fb7185,#f97316)"
                            : type === "warning"
                              ? "linear-gradient(90deg,#f59e0b,#fbbf24)"
                              : "linear-gradient(90deg,#0ea5e9,#60a5fa)",
                    }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(
    isMobile ? MobileToast : DesktopToast,
    document.getElementById("toast-root") || document.body
  );
}
